import React,{useEffect,useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import {FaceDetection} from '@mediapipe/face_detection';

export default function FaceCamera({open,onClose,onCapture}){
  const videoRef=useRef(null);
  const streamRef=useRef(null);
  const captureLock=useRef(false);
  const faceBoxRef=useRef(null);
  const [facing,setFacing]=useState('user');
  const [shot,setShot]=useState(null);
  const [error,setError]=useState('');
  const [status,setStatus]=useState('正在准备摄像头…');

  const stop=()=>{
    if(streamRef.current){
      streamRef.current.getTracks().forEach(t=>t.stop());
      streamRef.current=null;
    }
    if(videoRef.current)videoRef.current.srcObject=null;
  };

  // shot 变为 null 后，等待 video 节点重新挂载，再启动摄像头。
  useEffect(()=>{
    if(!open||shot)return;
    let cancelled=false;
    captureLock.current=false;
    setError('');
    setStatus('正在打开摄像头…');
    (async()=>{
      try{
        if(!navigator.mediaDevices?.getUserMedia)
          throw new Error('当前浏览器不支持摄像头');
        const stream=await navigator.mediaDevices.getUserMedia({
          audio:false,
          video:{facingMode:{ideal:facing},width:{ideal:1280},height:{ideal:1600}}
        });
        if(cancelled){stream.getTracks().forEach(t=>t.stop());return;}
        streamRef.current=stream;
        const video=videoRef.current;
        if(video){
          video.srcObject=stream;
          await video.play();
        }
        if(!cancelled)setStatus('正在启动人脸检测…');
      }catch(e){
        if(!cancelled){
          console.error(e);
          setError('无法打开摄像头，请使用 HTTPS 并允许摄像头权限。');
          setStatus('可尝试重新进入拍摄页面');
        }
      }
    })();
    return ()=>{cancelled=true;stop();};
  },[open,facing,shot]);

  const take=()=>{
    if(captureLock.current)return;
    const video=videoRef.current;
    if(!video||!video.videoWidth)return;
    captureLock.current=true;
    const scale=Math.min(1,1280/video.videoWidth);
    const canvas=document.createElement('canvas');
    canvas.width=Math.round(video.videoWidth*scale);
    canvas.height=Math.round(video.videoHeight*scale);
    const ctx=canvas.getContext('2d');
    if(!ctx){captureLock.current=false;return;}
    const face=faceBoxRef.current;

    if(face){
      const vw=video.videoWidth;
      const vh=video.videoHeight;
      const fw=face.w*vw;
      const fh=face.h*vh;
      const cx=face.x*vw;
      const cy=face.y*vh;

      // 3:4 竖版照片，保留头顶、下巴和部分肩膀
      const ratio=3/4;
      let cropH=Math.max(fh*1.9,fw*1.8/ratio);
      let cropW=cropH*ratio;

      const fit=Math.min(1,vw/cropW,vh/cropH);
      cropW*=fit;
      cropH*=fit;

      const sx=Math.max(
        0,Math.min(vw-cropW,cx-cropW/2)
      );
      const sy=Math.max(
        0,Math.min(vh-cropH,cy-cropH*0.56)
      );

      const outputScale=Math.min(1,960/cropW);
      canvas.width=Math.max(
        1,Math.round(cropW*outputScale)
      );
      canvas.height=Math.max(
        1,Math.round(cropH*outputScale)
      );

      ctx.drawImage(
        video,
        sx,sy,cropW,cropH,
        0,0,canvas.width,canvas.height
      );
    }else{
      ctx.drawImage(
        video,0,0,canvas.width,canvas.height
      );
    }
    canvas.toBlob(blob=>{
      if(!blob){
        captureLock.current=false;
        setError('照片生成失败，请重新拍摄。');
        return;
      }
      const url=URL.createObjectURL(blob);
      setShot({blob,url});
      stop();
    },'image/jpeg',0.9);
  };

  // MediaPipe 在浏览器本地检测；检测模型及 WASM 从本站加载。
  useEffect(()=>{
    if(!open||shot)return;
    let active=true;
    let detector=null;
    let frame=0;
    let busy=false;
    let lastRun=0;
    let steadySince=0;
    let lastBox=null;

    const hint=msg=>setStatus(old=>old===msg?old:msg);
    const reset=msg=>{steadySince=0;lastBox=null;faceBoxRef.current=null;hint(msg);};

    try{
      detector=new FaceDetection({
        locateFile:file=>'/mediapipe/face_detection/'+file
      });
      detector.setOptions({model:'short',minDetectionConfidence:0.75});
      detector.onResults(result=>{
        if(!active||captureLock.current)return;
        const faces=result?.detections||[];
        if(faces.length!==1){
          reset(faces.length>1?'请保持画面中只有一人':'请将人脸移入取景框');
          return;
        }
        const box=faces[0].boundingBox;
        const video=videoRef.current;
        const stage=video?.parentElement;
        if(!box||!video?.videoWidth||!stage){reset('正在定位人脸…');return;}

        // 将视频原始坐标映射到 object-fit:cover 后的屏幕坐标。
        const rect=stage.getBoundingClientRect();
        if(!rect.width||!rect.height)return;
        const scale=Math.max(rect.width/video.videoWidth,rect.height/video.videoHeight);
        const dw=video.videoWidth*scale,dh=video.videoHeight*scale;
        const cropX=(dw-rect.width)/2,cropY=(dh-rect.height)/2;
        const normalized=box.width<=1.5&&box.height<=1.5;
        const bx=normalized?box.xCenter:box.xCenter/video.videoWidth;
        const by=normalized?box.yCenter:box.yCenter/video.videoHeight;
        const bw=normalized?box.width:box.width/video.videoWidth;
        const bh=normalized?box.height:box.height/video.videoHeight;
        const x=(bx*dw-cropX)/rect.width;
        const y=(by*dh-cropY)/rect.height;
        const w=bw*dw/rect.width;
        const h=bh*dh/rect.height;
        if(![x,y,w,h].every(Number.isFinite))return;

        if(w<0.50||h<0.50){reset('请靠近一点，让脸占满取景框');return;}
        if(w>0.83||h>0.84||x-w/2<0.04||x+w/2>0.96||y-h/2<0.08||y+h/2>0.94){
          reset('请稍微后退，完整露出头部');return;
        }
        if(Math.abs(x-0.5)>0.13||Math.abs(y-0.51)>0.17){
          reset('请把脸移到取景框中央');return;
        }

        // 有眼睛和鼻尖关键点时，额外检查正脸与头部倾斜。
        const lm=faces[0].landmarks;
        if(Array.isArray(lm)&&lm.length>=3){
          const a=lm[0],b=lm[1],nose=lm[2];
          const ex=Math.abs(a.x-b.x);
          if(ex>0.001){
            const mid=(a.x+b.x)/2;
            if(Math.abs(a.y-b.y)>ex*0.24||Math.abs(nose.x-mid)>ex*0.33){
              reset('请正对镜头，保持头部端正');return;
            }
          }
        }

        faceBoxRef.current={x:bx,y:by,w:bw,h:bh};
        const now=performance.now();
        if(lastBox&&(Math.abs(x-lastBox.x)>0.055||Math.abs(y-lastBox.y)>0.055||Math.abs(w-lastBox.w)>0.065)){
          steadySince=0;
        }
        lastBox={x,y,w};
        if(!steadySince)steadySince=now;
        const remaining=Math.max(0,1.1-(now-steadySince)/1000);
        if(remaining>0){hint('位置合适，请保持不动 '+remaining.toFixed(1)+' 秒');return;}
        hint('正在自动拍照…');
        take();
      });
    }catch(e){
      console.error('人脸检测初始化失败',e);
      hint('自动检测不可用，请使用手动拍照');
      return ()=>{active=false;};
    }

    const loop=async now=>{
      if(!active)return;
      frame=requestAnimationFrame(loop);
      const video=videoRef.current;
      if(busy||captureLock.current||!video||video.readyState<2||!streamRef.current||now-lastRun<250)return;
      busy=true;
      lastRun=now;
      try{await detector.send({image:video});}
      catch(e){
        if(active){
          console.error('人脸检测失败',e);
          hint('自动检测不可用，请使用手动拍照');
          active=false;
          cancelAnimationFrame(frame);
        }
      }finally{busy=false;}
    };
    frame=requestAnimationFrame(loop);
    return ()=>{
      active=false;
      cancelAnimationFrame(frame);
      try{const result=detector?.close();result?.catch?.(()=>{});}catch(e){}
    };
  },[open,shot,facing]);

  if(!open)return null;

  const confirm=e=>{
    e?.preventDefault();
    e?.stopPropagation();
    if(!shot)return;
    const file=new File([shot.blob],`face_${Date.now()}.jpg`,{type:'image/jpeg'});
    onCapture?.(file,shot.url);
  };
  const retry=e=>{
    e?.preventDefault();
    e?.stopPropagation();
    if(shot?.url)URL.revokeObjectURL(shot.url);
    setShot(null);
  };
  const close=e=>{
    e?.preventDefault();
    e?.stopPropagation();
    stop();
    if(shot?.url)URL.revokeObjectURL(shot.url);
    setShot(null);
    onClose?.();
  };

  return createPortal(<div style={s.page} onClick={e=>e.stopPropagation()}>
    <div style={s.stage}>
      {shot
        ? <img src={shot.url} alt="拍摄预览" style={s.media}/>
        : <video ref={videoRef} playsInline muted style={{...s.media,transform:facing==='user'?'scaleX(-1)':'none'}}/>
      }
      <div style={s.top}>
        <button type="button" style={s.back} onClick={close}>‹</button>
        <div>
          <div style={s.title}>{shot?'请确认照片清晰完整':'请将人脸置于取景框中'}</div>
          <div style={s.sub}>正面免冠 · 光线均匀 · 请勿逆光</div>
        </div>
      </div>
      {!shot&&<div style={s.mask}>
        <svg viewBox="0 0 300 390" style={s.faceSvg} aria-hidden="true">
          <path d="M150 24 C82 24 58 78 60 151 C61 187 69 211 84 238 C91 251 97 265 96 286 C95 307 88 324 73 338 L30 369 M150 24 C218 24 242 78 240 151 C239 187 231 211 216 238 C209 251 203 265 204 286 C205 307 212 324 227 338 L270 369" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round"/>
        </svg>
      </div>}
      {!shot&&!error&&<div style={s.status}>{status}</div>}
      {error&&<div style={s.error}>{error}</div>}
    </div>
    <div style={s.bottom}>
      {shot ? <>
        <button type="button" style={s.smallBtn} onClick={retry}>重新拍摄</button>
        <button type="button" style={s.confirm} onClick={confirm}>使用照片</button>
      </> : <>
        <div style={{width:78}}/>
        <button type="button" style={s.shutter} onClick={take} aria-label="手动拍照"><span/></button>
        <button type="button" style={s.switchBtn} onClick={()=>setFacing(v=>v==='user'?'environment':'user')}>↻<small>切换</small></button>
      </>}
    </div>
  </div>,document.body);
}

const s={
  page:{position:'fixed',inset:0,zIndex:99999,background:'#111',display:'flex',flexDirection:'column'},
  stage:{position:'relative',flex:1,minHeight:0,overflow:'hidden',background:'#111'},
  media:{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'},
  top:{position:'absolute',left:0,right:0,top:0,zIndex:4,color:'#fff',display:'grid',gridTemplateColumns:'48px 1fr 48px',alignItems:'start',padding:'max(16px,env(safe-area-inset-top)) 16px 10px',textAlign:'center',background:'linear-gradient(#0009,transparent)'},
  back:{border:0,background:'transparent',color:'#fff',fontSize:48,lineHeight:1,padding:0},
  title:{fontSize:22,fontWeight:800,marginTop:10,textShadow:'0 1px 3px #000'},
  sub:{fontSize:14,marginTop:8,opacity:.95,textShadow:'0 1px 3px #000'},
  mask:{position:'absolute',inset:3,zIndex:3,pointerEvents:'none'},
  faceSvg:{position:'absolute',left:'50%',top:'78%',transform:'translate(-50%,-50%)',width:'185%',maxWidth:'none',height:'150%',filter:'drop-shadow(0 1px 2px #0008)'},
  status:{position:'absolute',left:14,right:14,bottom:18,zIndex:5,textAlign:'center',background:'#0009',color:'#fff',padding:'10px 12px',borderRadius:10,fontSize:16,fontWeight:600},
  error:{position:'absolute',left:18,right:18,bottom:22,zIndex:5,background:'#a00d',color:'#fff',padding:12,borderRadius:10,textAlign:'center'},
  bottom:{minHeight:150,background:'#090909',display:'flex',alignItems:'center',justifyContent:'space-around',gap:18,padding:'16px 24px max(20px,env(safe-area-inset-bottom))',boxSizing:'border-box'},
  shutter:{width:82,height:82,borderRadius:'50%',border:'4px solid #fff',background:'transparent',padding:6,boxShadow:'0 0 0 2px #777'},
  switchBtn:{width:78,border:0,background:'transparent',color:'#fff',fontSize:42,display:'flex',flexDirection:'column',alignItems:'center'},
  smallBtn:{border:'1px solid #777',background:'#222',color:'#fff',borderRadius:12,padding:'14px 18px',fontSize:16},
  confirm:{border:0,background:'#1677ff',color:'#fff',borderRadius:12,padding:'14px 28px',fontSize:17,fontWeight:800}
};
